import React, { createContext, useCallback, useMemo, useState, ReactNode } from 'react';
import { Alert, Snackbar } from '@mui/material';
import type { AlertColor } from '@mui/material';

export interface NotificationsContextType {
  /** Show a transient notification. Renders above open dialogs. */
  notify: (message: string, severity?: AlertColor) => void;
}

/**
 * Safe no-op default so components can call `useNotify()` even when the
 * provider is not mounted (e.g. in unit tests using renderWithProviders,
 * which wraps Auth/Theme/Router only). This keeps the hook non-throwing.
 */
export const NotificationsContext = createContext<NotificationsContextType>({
  notify: () => {},
});

interface ActiveNotification {
  key: number;
  message: string;
  severity: AlertColor;
}

interface NotificationsProviderProps {
  children: ReactNode;
}

/**
 * Global snackbar layer. The MUI Snackbar renders in a portal at
 * `theme.zIndex.snackbar` (1400), above the modal backdrop (1300), so failures
 * raised from inside an open dialog are always visible to the user.
 */
export const NotificationsProvider: React.FC<NotificationsProviderProps> = ({ children }) => {
  const [current, setCurrent] = useState<ActiveNotification | null>(null);
  const [open, setOpen] = useState(false);

  const notify = useCallback((message: string, severity: AlertColor = 'error') => {
    setCurrent({ key: Date.now(), message, severity });
    setOpen(true);
  }, []);

  const handleClose = useCallback((_event?: unknown, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  }, []);

  const value = useMemo(() => ({ notify }), [notify]);

  return (
    <NotificationsContext.Provider value={value}>
      {children}
      <Snackbar
        key={current?.key}
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {current ? (
          <Alert
            onClose={handleClose}
            severity={current.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {current.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </NotificationsContext.Provider>
  );
};
